import { CoreEventType, type CoreEvent } from '@good-season/stats-core';

/**
 * The basketball event registry.
 *
 * Every type in BUILD_PLAN.md section 5.3, plus the control types re-exported
 * from core so a caller has one list to work from. This is the file a second
 * sport would replace wholesale.
 */

export const ShotEventType = {
  FG2_MADE: 'FG2_MADE',
  FG2_MISS: 'FG2_MISS',
  FG3_MADE: 'FG3_MADE',
  FG3_MISS: 'FG3_MISS',
  FT_MADE: 'FT_MADE',
  FT_MISS: 'FT_MISS',
} as const;

export const PossessionEventType = {
  REB_OFF: 'REB_OFF',
  REB_DEF: 'REB_DEF',
  REB_TEAM: 'REB_TEAM',
  AST: 'AST',
  STL: 'STL',
  BLK: 'BLK',
  TO: 'TO',
  DEFLECTION: 'DEFLECTION',
  CHARGE_TAKEN: 'CHARGE_TAKEN',
} as const;

export const FoulEventType = {
  FOUL_PERSONAL: 'FOUL_PERSONAL',
  FOUL_SHOOTING: 'FOUL_SHOOTING',
  FOUL_OFFENSIVE: 'FOUL_OFFENSIVE',
  FOUL_TECHNICAL: 'FOUL_TECHNICAL',
  FOUL_FLAGRANT: 'FOUL_FLAGRANT',
} as const;

export const BasketballControlEventType = {
  ...CoreEventType,
  JUMP_BALL: 'JUMP_BALL',
} as const;

export const AnnotationEventType = {
  NOTE: 'NOTE',
} as const;

export const BasketballEventType = {
  ...ShotEventType,
  ...PossessionEventType,
  ...FoulEventType,
  ...BasketballControlEventType,
  ...AnnotationEventType,
} as const;

export type BasketballEventType = (typeof BasketballEventType)[keyof typeof BasketballEventType];

const ALL_TYPES: ReadonlySet<string> = new Set(Object.values(BasketballEventType));

export function isBasketballEventType(type: string): type is BasketballEventType {
  return ALL_TYPES.has(type);
}

/**
 * Points an event is worth.
 *
 * This is the function injected into stats-core's plus-minus. It is also the
 * single definition of what scores, which is why the scoreboard and the box
 * score can never disagree: there is nowhere else for a number to come from.
 *
 * A technical or flagrant foul awards free throws, which arrive as their own
 * FT_MADE events. The foul itself is worth nothing, and adding points here
 * would double-count them.
 */
export function pointsOf(event: CoreEvent): number {
  switch (event.type) {
    case ShotEventType.FG2_MADE:
      return 2;
    case ShotEventType.FG3_MADE:
      return 3;
    case ShotEventType.FT_MADE:
      return 1;
    default:
      return 0;
  }
}

/** A foul that counts against a player's personal foul total for disqualification. */
export function isPersonalFoul(type: string): boolean {
  return (
    type === FoulEventType.FOUL_PERSONAL ||
    type === FoulEventType.FOUL_SHOOTING ||
    type === FoulEventType.FOUL_OFFENSIVE ||
    type === FoulEventType.FOUL_FLAGRANT
  );
}

/**
 * A foul that counts toward the team foul total for bonus purposes.
 *
 * Personal, shooting, flagrant, and technical fouls charged to a player all
 * count toward the team total under NFHS rules, which is what youth leagues
 * follow. An OFFENSIVE foul is the exception: it is charged to the player but
 * does not put the other team in the bonus.
 *
 * An earlier version admitted only personal and shooting, which under-counted
 * on every flagrant and every technical. Getting this wrong makes the bonus
 * indicator lie, and the bonus indicator is one of the few things a scorekeeper
 * is asked to announce out loud, which is why it gets its own function rather
 * than being folded into the personal foul check.
 *
 * BUILD_PLAN does not define the composition, so this is a domain-rules call
 * rather than a plan divergence. If your league differs, this is the one place
 * to change it.
 */
export function countsTowardTeamFouls(type: string): boolean {
  return (
    type === FoulEventType.FOUL_PERSONAL ||
    type === FoulEventType.FOUL_SHOOTING ||
    type === FoulEventType.FOUL_FLAGRANT ||
    type === FoulEventType.FOUL_TECHNICAL
  );
}

/**
 * A foul that appears in a player's PF column.
 *
 * Everything charged to the player, which is every foul type. Distinct from
 * `isPersonalFoul`, which governs DISQUALIFICATION and deliberately excludes
 * technicals.
 *
 * These were the same predicate plus an inline `|| FOUL_TECHNICAL` at the box
 * score call site, so the exported helper and the column it was named after
 * disagreed. Any future caller reaching for `isPersonalFoul` to build a PF
 * column would have produced a number that did not match the box score.
 */
export function countsAsPlayerFoul(type: string): boolean {
  return isPersonalFoul(type) || type === FoulEventType.FOUL_TECHNICAL;
}

/** A field goal attempt. Three-pointers are field goals, per every box score ever printed. */
export function isFieldGoalAttempt(type: string): boolean {
  return (
    type === ShotEventType.FG2_MADE ||
    type === ShotEventType.FG2_MISS ||
    type === ShotEventType.FG3_MADE ||
    type === ShotEventType.FG3_MISS
  );
}

export function isThreeAttempt(type: string): boolean {
  return type === ShotEventType.FG3_MADE || type === ShotEventType.FG3_MISS;
}

export function isFreeThrowAttempt(type: string): boolean {
  return type === ShotEventType.FT_MADE || type === ShotEventType.FT_MISS;
}
